import { AfterViewInit, Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { toObservable } from '@angular/core/rxjs-interop';
import { Move } from '@app/move/interfaces/move';
import { WaterTransferAssetType } from '@ui/water/enums/water-transfer-asset-type.enum';
import { ArrayUtils, DbUtils, EnergyMetersState, FileUtils, Mimetypes, ObjectUtils, PostHogEventsEnum, RxjsComponent } from '@smooved/core';
import { ButtonAppearance, CheckInput, FormUtils, gasControlNames, SvgIllustration, UiContext, UiLabelPosition } from '@smooved/ui';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { WaterDetailFields } from './water-details.constants';
import { WaterMeterStore } from '@smooved-lib/lib/meters/store/water-meter.store';
import { MoveUtils } from '@app/move/state/move.utils';
import { MoveSandbox } from '@app/move/sandboxes/move.sandbox';
import { MoveService } from '@app/move/services/move.service';
import { formControlNames } from '@app/real-estate-group/modals/location-form/location-form.constants';
import { MoverRole } from '@app/move/enums/mover-role.enum';
import { PosthogServicesService } from '@app/tracking/posthog-services.service';
import { PosthogService } from '@app/tracking/posthog.service';
import { ServiceType } from '@app/services/types/service-type';
import { WaterTransferDocumentServiceStore } from '@app/services/water-transfer-document/store/water-transfer-document-service.store';
import { WaterCompositeStore } from '@app/services/water-composite/store/water-composite.store';
import { WaterFinalInvoiceServiceStore } from '@app/services/water-final-invoice/store/water-final-invoice-service.store';
import { FilingOptionsStore } from '@app/services/filing-options/store/filing-options.store';
import { WaterTransferDocumentServiceStatus } from '@app/services/water-transfer-document/enums/water-transfer-document-service-status.enum';

@Component({
    selector: 'smvd-app-water-details-modal',
    templateUrl: 'water-details.modal.html',
    styleUrls: ['./water-details.modal.scss'],
})
export class WaterDetailsModalComponent extends RxjsComponent implements OnInit, AfterViewInit {
    @Input() public move: Move;
    @ViewChild('fileInput') fileInput: ElementRef;

    public transferee: Move;
    public leaver: Move;

    public lookupToolUrl = '/water/lookup-tool';

    public loading = false;
    public uploader;

    public readonly waterCompositeStore = inject(WaterCompositeStore);
    public readonly waterTransferDocumentServiceStore = inject(WaterTransferDocumentServiceStore);
    public readonly waterFinalInvoiceServiceStore = inject(WaterFinalInvoiceServiceStore);
    public readonly filingOptionsStore = inject(FilingOptionsStore);
    public readonly waterMeterStore = inject(WaterMeterStore);
    public readonly posthogServicesService = inject(PosthogServicesService);
    public readonly posthogService = inject(PosthogService);

    private readonly waterTransferDocumentService$ = toObservable(this.waterCompositeStore.waterTransferDocumentService);
    private readonly waterFinalInvoiceService$ = toObservable(this.waterCompositeStore.waterFinalInvoiceService);
    private readonly waterMeter$ = toObservable(this.waterMeterStore.meter);

    public readonly form = this.fb.group({
        [WaterDetailFields.WaterSupplier]: [null],
        [WaterDetailFields.NationalRegistrationNumber]: null,
        [WaterDetailFields.MeterNumber]: [null],
        [WaterDetailFields.MeterReading]: [null],
        [WaterDetailFields.leaverFilingOptionId]: [null],
        [WaterDetailFields.LeaverFinalInvoiceByPost]: [null],
        [WaterDetailFields.LeaverContactAddress]: [null],
        [WaterDetailFields.TransferDocumentComplete]: [null],
    });

    public readonly waterDetailFields = WaterDetailFields;

    public readonly context = UiContext;
    public readonly buttonAppearance = ButtonAppearance;
    public readonly SvgIllustration = SvgIllustration;
    public readonly Mimetypes = Mimetypes;
    public readonly EnergyMetersState = EnergyMetersState;
    public readonly svgIllustration = SvgIllustration;
    public readonly WaterTransferAssetType = WaterTransferAssetType;
    public readonly uiContext = UiContext;
    public readonly DbUtils = DbUtils;

    private filesChangedSubject = new Subject<void>();

    constructor(
        private readonly dialogRef: MatDialogRef<any>,
        private readonly fb: FormBuilder,
        private readonly moveSandbox: MoveSandbox,
        private readonly moveService: MoveService,
    ) {
        super();
    }

    public ngOnInit(): void {
        const { transferee, leaver } = MoveUtils.getMovers(this.move);
        this.transferee = transferee;
        this.leaver = leaver;

        combineLatest([this.waterTransferDocumentService$, this.waterMeter$, this.waterFinalInvoiceService$])
            .pipe(
                first(
                    ([waterTransferDocumentService, waterMeter, waterFinalInvoiceService]) =>
                        !!waterTransferDocumentService && !!waterMeter && !!waterFinalInvoiceService,
                ),
            )
            .subscribe(([waterTransferDocumentService, waterMeter, waterFinalInvoiceService]) => {
                this.form.patchValue({
                    [WaterDetailFields.WaterSupplier]: DbUtils.getStringId(waterTransferDocumentService.supplier),
                    [WaterDetailFields.NationalRegistrationNumber]: transferee.user?.nationalRegistrationNumber,
                    [WaterDetailFields.MeterNumber]: waterMeter.meterNumber,
                    [WaterDetailFields.MeterReading]: waterMeter.meterReading,
                    [WaterDetailFields.leaverFilingOptionId]: DbUtils.getStringId(waterFinalInvoiceService.filingOption),
                    [WaterDetailFields.LeaverFinalInvoiceByPost]: waterTransferDocumentService.leaverFinalInvoiceByPost,
                    [WaterDetailFields.LeaverContactAddress]: leaver?.user?.contactAddress,
                    [WaterDetailFields.TransferDocumentComplete]: !!waterTransferDocumentService.transferDocumentComplete,
                });

                this.uploader = FileUtils.getFileUploader();
                const uploadedAssets = [...(waterTransferDocumentService?.waterTransferAssets ?? [])] as unknown as File[];
                this.uploader.addToQueue(uploadedAssets);
            });
    }

    public ngAfterViewInit(): void {
        this.filesChangedSubject.next();
    }

    public onSupplierChanged(supplierId: string): void {
        this.filingOptionsStore.loadFilingOptions(this.waterCompositeStore.waterFinalInvoiceService()?.serviceTypeId, supplierId);
    }

    public async onGenerateWaterTransferDocument(): Promise<void> {
        const transactionId = DbUtils.getStringId(this.transferee);
        try {
            await this.saveWaterDetails(false);
            this.loading = true;
            await this.waterTransferDocumentServiceStore.generateWaterTransferDocument(transactionId).catch(() => (this.loading = false));
            this.loading = false;
            this.posthogService.sendEventForTransaction(PostHogEventsEnum.GenerateWaterTransferDoc, transactionId);
        } catch (error) {
            this.posthogService.sendEventForTransaction(PostHogEventsEnum.GenerateWaterTransferDoc, transactionId, {}, error);
        }
    }

    public async saveWaterDetails(closeModal: boolean = true): Promise<void> {
        this.form.markAllAsTouched();
        if (!this.form.valid) return Promise.resolve();

        this.loading = true;

        const touchedValues = FormUtils.getTouchedValues(this.form);

        const hasWaterSupplier = ObjectUtils.has(touchedValues, 'waterSupplier');
        const hasLeaverFinalInvoiceByPost = ObjectUtils.has(touchedValues, 'leaverFinalInvoiceByPost');
        const hasTransferDocumentComplete = ObjectUtils.has(touchedValues, 'transferDocumentComplete');
        const waterServicePayload: any = {};
        if (hasWaterSupplier) waterServicePayload.supplier = touchedValues.waterSupplier;
        if (hasLeaverFinalInvoiceByPost) waterServicePayload.leaverFinalInvoiceByPost = touchedValues.leaverFinalInvoiceByPost;
        if (hasTransferDocumentComplete) waterServicePayload.transferDocumentComplete = touchedValues.transferDocumentComplete;

        const waterMeterPayload: any = {};
        const hasMeterNumber = ObjectUtils.has(touchedValues, 'meterNumber');
        const hasMeterReading = ObjectUtils.has(touchedValues, 'meterReading');
        if (hasMeterNumber) waterMeterPayload.meterNumber = touchedValues.meterNumber;
        if (hasMeterReading) waterMeterPayload.meterReading = touchedValues.meterReading;

        // Create an array to hold all the property update promises
        const updatePromises: Promise<any>[] = [];

        // Add leaver's contactAddress update if leaver exists
        if (this.leaver) {
            const patchRequest = {
                user: {
                    contactAddress: this.form.value.leaverContactAddress,
                },
            };
            ObjectUtils.removeEmpty(patchRequest, true);
            updatePromises.push(firstValueFrom(this.moveService.patch(DbUtils.getStringId(this.leaver), patchRequest, true)));
        }

        const patchRequest = {
            user: {
                nationalRegistrationNumber: this.form.value.nationalRegistrationNumber as string,
            },
        };
        ObjectUtils.removeEmpty(patchRequest, true);
        updatePromises.push(firstValueFrom(this.moveService.patch(DbUtils.getStringId(this.transferee), patchRequest, true)));

        const results = await Promise.all(updatePromises);
        let updatedMove;
        if (this.move.user.role === MoverRole.Transferee) {
            updatedMove = results[ArrayUtils.getLength(results) - 1];
            if (ArrayUtils.getLength(results) > 1) {
                updatedMove.linkedMove.user.contactAddress = results[0]?.user.contactAddress;
            }
        } else {
            updatedMove = results[0];
            updatedMove.linkedMove.user.nationalRegistrationNumber = results[1]?.user.nationalRegistrationNumber;
        }
        this.moveSandbox.setMoveState(updatedMove);

        const posthogPropertiesForUpdate: any = {
            manualInputAdded: true,
            filingOption: this.filingOptionsStore
                .filingOptions()
                .find((filingOption) => DbUtils.getStringId(filingOption) === touchedValues.leaverFilingOptionId)?.filingType,
            statusBeforeSave: WaterTransferDocumentServiceStatus[this.waterCompositeStore.waterTransferDocumentService().status],
        };
        try {
            await Promise.all([
                this.waterMeterStore.patch(DbUtils.getStringId(this.transferee), waterMeterPayload),
                this.waterTransferDocumentServiceStore.patch(DbUtils.getStringId(this.transferee), waterServicePayload),
                this.waterFinalInvoiceServiceStore.updateFilingOption(
                    DbUtils.getStringId(this.transferee),
                    this.form.get(WaterDetailFields.leaverFilingOptionId).value,
                ),
            ]);
            this.loading = false;

            posthogPropertiesForUpdate.statusAfterSave =
                WaterTransferDocumentServiceStatus[this.waterTransferDocumentServiceStore.waterTransferDocumentService().status];

            this.posthogServicesService.sendEvent(
                PostHogEventsEnum.SaveMeterReadings,
                ServiceType.WaterTransfer,
                posthogPropertiesForUpdate,
            );
            if (closeModal) {
                this.dialogRef.close(updatedMove);
            }
        } catch (error) {
            this.posthogServicesService.sendEvent(
                PostHogEventsEnum.SaveMeterReadings,
                ServiceType.WaterTransfer,
                posthogPropertiesForUpdate,
                error,
            );
        }
    }

    public onAssetChanged(): void {
        this.waterCompositeStore.load(DbUtils.getStringId(this.transferee));
    }

    protected readonly fields = gasControlNames;
    protected readonly ArrayUtils = ArrayUtils;
    protected readonly formControlNames = formControlNames;
    protected readonly UiLabelPosition = UiLabelPosition;

    public transferDocumentCompleteOption: CheckInput<boolean> = {
        id: 'transfer-document-complete',
        name: 'transferDocumentComplete',
        value: true,
        labelResource: 'V2.SERVICES.WATER.ASSETS.TRANSFER_DOCUMENT.DOCUMENT_COMPLETE',
    };
}
